import _ from "lodash";
import { action, autorun, computed, extendObservable, reaction } from "mobx";
import moment from "moment";
import { DateFormat } from "../utils/format";
import { isNumber } from "../utils/numbers";
import { resetRangeDefaults } from "../utils/defaults";

class StrikeZoneTableStore {
  constructor(setiApi, routerStore) {
    this.setiApi = setiApi;
    this.routerStore = routerStore;
    this.setiData = {};
    this.errors = {};
    this.defaults = {
      pitchTypes: [],
      hardballsCheckboxGroup: ["hardballs"],
      startDate: moment()
        .subtract(1, "year")
        .format(DateFormat.PITCH_CRITERIA_DATE),
      endDate: moment().format(DateFormat.PITCH_CRITERIA_DATE),
      lastNGames: "10",
      lowZRange: "2.0",
      highZRange: "3.0",
      lowXRange: "-2.0",
      highXRange: "2.0",
      lowBucketRange: "-1.5",
      highBucketRange: "1.5",
      bucketStep: "0.1",
      bucketVariance: "0.1",
      plateAxis: "X",
      selectedBatterSide: "B",
      selectedPitcherHand: "B",
      selectedTimeOfDay: "B",
      venueIds: "",
      pivotKey: "VENUE",
      minimumPitchCount: 300
    };

    extendObservable(this, {
      isLoading: true,
      pitchTypes: this.defaults["pitchTypes"],
      hardballsCheckboxGroup: this.defaults["hardballsCheckboxGroup"],
      startDate: this.defaults["startDate"],
      endDate: this.defaults["endDate"],
      lastNGames: this.defaults["lastNGames"],
      lowZRange: this.defaults["lowZRange"],
      highZRange: this.defaults["highZRange"],
      lowXRange: this.defaults["lowXRange"],
      highXRange: this.defaults["highXRange"],
      lowBucketRange: this.defaults["lowBucketRange"],
      highBucketRange: this.defaults["highBucketRange"],
      bucketStep: this.defaults["bucketStep"],
      bucketVariance: this.defaults["bucketVariance"],
      plateAxis: this.defaults["plateAxis"],
      selectedBatterSide: this.defaults["selectedBatterSide"],
      selectedPitcherHand: this.defaults["selectedPitcherHand"],
      selectedTimeOfDay: this.defaults["selectedTimeOfDay"],
      venueIds: this.defaults["venueIds"],
      pivotKey: this.defaults["pivotKey"],
      minimumPitchCount: this.defaults["minimumPitchCount"],
      venueIdsParam: computed(
        () => (_.isEmpty(this.venueIds) ? [] : this.venueIds.split(","))
      ),
      hardballsOnly: computed(() => {
        if (this.hardballsCheckboxGroup.length === 0) {
          return false;
        } else {
          return true;
        }
      }),
      isFormValid: computed(() => {
        return (
          this.validateDate(this.startDate) &&
          this.validateDate(this.endDate) &&
          this.isBlankOrPositiveNumber(this.lastNGames) &&
          this.isPositiveNumber(this.lowZRange) &&
          this.isPositiveNumber(this.highZRange) &&
          isNumber(this.lowXRange) &&
          isNumber(this.highXRange) &&
          isNumber(this.lowBucketRange) &&
          isNumber(this.highBucketRange) &&
          isNumber(this.bucketStep) &&
          isNumber(this.bucketVariance)
        );
      }),
      params: computed(() => ({
        pitchCriteria: {
          pitchTypes: this.pitchTypes,
          hardballsOnly: this.hardballsOnly,
          lastNGames: this.lastNGames,
          dayNightCode: this.selectedTimeOfDay,
          batterSide: this.selectedBatterSide,
          pitcherHand: this.selectedPitcherHand,
          venueIds: this.venueIdsParam,
          dateRange: {
            low: this.startDate,
            high: this.endDate,
            inclusive: true
          },
          zrange: {
            low: this.lowZRange,
            high: this.highZRange,
            inclusive: true
          },
          xrange: {
            low: this.lowXRange,
            high: this.highXRange,
            inclusive: true
          }
        },
        modelArgs: {
          bucketRange: {
            low: this.lowBucketRange,
            high: this.highBucketRange,
            inclusive: true
          },
          bucketStep: this.bucketStep,
          bucketVariance: this.bucketVariance,
          pivotKey: this.pivotKey,
          plateAxis: this.plateAxis,
          minimumPitchCount: this.minimumPitchCount
        }
      })),
      // Utility function to reset defaults based on east-west / north-south plate axis
      resetModelArgDefaults: action(() => resetRangeDefaults(this)),
      updateStartDate: action(value => {
        this.startDate = value;
      }),
      updateEndDate: action(value => {
        this.endDate = value;
      }),
      updateLastNGames: action(value => {
        this.lastNGames = value;
      }),
      updateLowXRange: action(value => {
        this.lowXRange = value;
      }),
      updateHighXRange: action(value => {
        this.highXRange = value;
      }),
      updateLowZRange: action(value => {
        this.lowZRange = value;
      }),
      updateHighZRange: action(value => {
        this.highZRange = value;
      }),
      updatePitchTypes: action(values => {
        this.pitchTypes = values;
      }),
      updateLowBucketRange: action(value => {
        this.lowBucketRange = value;
      }),
      updateHighBucketRange: action(value => {
        this.highBucketRange = value;
      }),
      updateBucketStep: action(value => {
        this.bucketStep = value;
      }),
      updateBucketVariance: action(value => {
        this.bucketVariance = value;
      }),
      updatePlateAxis: action(value => {
        this.plateAxis = value;
        this.resetModelArgDefaults();
      }),
      updateHardballs: action(values => {
        this.hardballsCheckboxGroup = values;
      }),
      updateSelectedBatterSide: action(value => {
        this.selectedBatterSide = value;
      }),
      updateSelectedPitcherHand: action(value => {
        this.selectedPitcherHand = value;
      }),
      updateSelectedTimeOfDay: action(value => {
        this.selectedTimeOfDay = value;
      }),
      updateVenueIds: action(values => {
        this.venueIds = values;
      }),
      updatePivotKey: action(value => {
        this.pivotKey = value;
      }),
      updateMinimumPitchCount: action(value => {
        this.minimumPitchCount = value;
      }),
      fetchData: action(() => {
        if (this.isFormValid) {
          this.isLoading = true;
          this.setiApi
            .fetchStrikeZoneData(this.params)
            .then(data => {
              this.setiData = data;
            })
            .then(_ => {
              this.isLoading = false;
            })
            .catch(e => e);
        }
      }),
      urlParams: computed(() => {
        let params = "";
        params += `pitchTypes=${this.pitchTypes.join(",")}`;
        params += `&hardballsOnly=${this.hardballsOnly}`;
        params += `&venueIds=${this.venueIds}`;
        params += `&startDate=${this.startDate}`;
        params += `&endDate=${this.endDate}`;
        params += `&lastNGames=${this.lastNGames}`;
        params += `&batterSide=${this.selectedBatterSide}`;
        params += `&pitcherHand=${this.selectedPitcherHand}`;
        params += `&dayNightCode=${this.selectedTimeOfDay}`;
        params += `&lowZRange=${this.lowZRange}`;
        params += `&highZRange=${this.highZRange}`;
        params += `&lowXRange=${this.lowXRange}`;
        params += `&highXRange=${this.highXRange}`;
        params += `&lowBucketRange=${this.lowBucketRange}`;
        params += `&highBucketRange=${this.highBucketRange}`;
        params += `&bucketStep=${this.bucketStep}`;
        params += `&bucketVariance=${this.bucketVariance}`;
        params += `&pivotKey=${this.pivotKey}`;
        params += `&plateAxis=${this.plateAxis}`;
        params += `&minimumPitchCount=${this.minimumPitchCount}`;
        return params;
      }),
      updateFromUrlParams: action(search => {
        const params = new URLSearchParams(search);
        if (params.has("pitchTypes") && !_.isEmpty(params.get("pitchTypes"))) {
          this.pitchTypes = params.get("pitchTypes").split(",");
        } else {
          this.pitchTypes = this.defaults["pitchTypes"];
        }
        if (params.has("hardballsOnly")) {
          if (params.get("hardballsOnly") === "true") {
            this.hardballsCheckboxGroup = ["hardballs"];
          } else {
            this.hardballsCheckboxGroup = [];
          }
        } else {
          this.hardballsCheckboxGroup = this.defaults["hardballsCheckboxGroup"];
        }
        if (params.has("venueIds")) {
          this.venueIds = params.get("venueIds");
        } else {
          this.venueIds = this.defaults["venueIds"];
        }
        if (params.has("startDate")) {
          this.startDate = params.get("startDate");
        } else {
          this.startDate = this.defaults["startDate"];
        }
        if (params.has("endDate")) {
          this.endDate = params.get("endDate");
        } else {
          this.endDate = this.defaults["endDate"];
        }
        if (params.has("lastNGames")) {
          this.lastNGames = params.get("lastNGames");
        } else {
          this.lastNGames = this.defaults["lastNGames"];
        }
        if (params.has("batterSide")) {
          this.selectedBatterSide = params.get("batterSide");
        } else {
          this.selectedBatterSide = this.defaults["selectedBatterSide"];
        }
        if (params.has("pitcherHand")) {
          this.selectedPitcherHand = params.get("pitcherHand");
        } else {
          this.selectedPitcherHand = this.defaults["selectedPitcherHand"];
        }
        if (params.has("dayNightCode")) {
          this.selectedTimeOfDay = params.get("dayNightCode");
        } else {
          this.selectedTimeOfDay = this.defaults["selectedTimeOfDay"];
        }
        if (params.has("lowXRange")) {
          this.lowXRange = params.get("lowXRange");
        } else {
          this.lowXRange = this.defaults["lowXRange"];
        }
        if (params.has("highXRange")) {
          this.highXRange = params.get("highXRange");
        } else {
          this.highXRange = this.defaults["highXRange"];
        }
        if (params.has("lowZRange")) {
          this.lowZRange = params.get("lowZRange");
        } else {
          this.lowZRange = this.defaults["lowZRange"];
        }
        if (params.has("highZRange")) {
          this.highZRange = params.get("highZRange");
        } else {
          this.highZRange = this.defaults["highZRange"];
        }
        if (params.has("lowBucketRange")) {
          this.lowBucketRange = params.get("lowBucketRange");
        } else {
          this.lowBucketRange = this.defaults["lowBucketRange"];
        }
        if (params.has("highBucketRange")) {
          this.highBucketRange = params.get("highBucketRange");
        } else {
          this.highBucketRange = this.defaults["highBucketRange"];
        }
        if (params.has("bucketStep")) {
          this.bucketStep = params.get("bucketStep");
        } else {
          this.bucketStep = this.defaults["bucketStep"];
        }
        if (params.has("bucketVariance")) {
          this.bucketVariance = params.get("bucketVariance");
        } else {
          this.bucketVariance = this.defaults["bucketVariance"];
        }
        this.plateAxis = params.has("plateAxis")
          ? params.get("plateAxis")
          : this.defaults["plateAxis"];

        this.pivotKey = params.has("pivotKey")
          ? params.get("pivotKey")
          : this.defaults["pivotKey"];

        this.minimumPitchCount = params.has("minimumPitchCount")
          ? params.get("minimumPitchCount")
          : this.defaults["minimumPitchCount"];
      })
    });

    reaction(() => this.params, params => this.updateErrors());
    autorun(() => {
      if (this.routerStore.isStrikeZoneTableTab) {
        this.updateFromUrlParams(this.routerStore.location.search);
        this.fetchData();
      }
    });
  }

  validateDate(date) {
    if (date === "") {
      return true;
    } else {
      const dateFormat = /^\d{4}-\d{2}-\d{2}$/;
      return dateFormat.test(date);
    }
  }

  updateErrors() {
    this.updateError("startDate", this.validateDate);
    this.updateError("endDate", this.validateDate);
    this.updateError("lowZRange", this.isPositiveNumber);
    this.updateError("highZRange", this.isPositiveNumber);
    this.updateError("lowXRange", isNumber);
    this.updateError("highXRange", isNumber);
    this.updateError("lowBucketRange", isNumber);
    this.updateError("highBucketRange", isNumber);
    this.updateError("bucketStep", isNumber);
    this.updateError("bucketVariance", isNumber);
    this.updateError("lastNGames", this.isBlankOrPositiveNumber);
  }

  updateError(property, validator) {
    const v = validator.bind(this);
    if (v(this[property])) {
      this.errors[property] = false;
    } else {
      this.errors[property] = true;
    }
  }

  isBlankOrPositiveNumber(input) {
    return _.isEmpty(input) || this.isPositiveNumber(input);
  }

  isPositiveNumber(input) {
    if (isNumber(input)) {
      return parseFloat(input) > 0 ? true : false;
    } else {
      return false;
    }
  }
}

export default StrikeZoneTableStore;



// WEBPACK FOOTER //
// ./src/stores/strikeZoneTableStore.js